import { useRef, useState } from "react";
import { X, Check, Paperclip, Trash2 } from "lucide-react";
import BottomSheet from "../common/BottomSheet";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { tenderService } from "../../services/api";
import useTranslate from "../../hooks/useTranslate";

// D-011 apply modal. A lawyer applies to a STAFF request with a short cover
// letter + an optional CV. No player selection and no transfer fee — those are
// agent-only. Submits the existing pitch-create endpoint (createStaffPitch =
// POST /dashboard/agent/pitches/create/, tender_id + content + document). The
// backend rejects any non-staff tender with 403, so this modal is only ever
// opened from the staff-only lawyer feed.
// Matches the platform pitch-document upload (PitchPlayer.js: "PDFs only, up to 50MB").
const MAX_CV_MB = 50;

const LawyerApplyModal = ({ tender, onClose, onApplied, t: tProp }) => {
  const { t: tHook } = useTranslate();
  const t = tProp || tHook;
  const isMobile = useMediaQuery("(max-width: 768px)");

  const fileRef = useRef(null);
  const [content, setContent] = useState("");
  const [cv, setCv] = useState(null);
  const [fileError, setFileError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { done } | { error }

  if (!tender) return null;

  const positionLabel = (tender.position || "")
    .split("_")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ""))
    .join(" ");
  const clubName = tender.club?.name || "";

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    setFileError("");
    if (!f) {
      setCv(null);
      return;
    }
    if (f.size > MAX_CV_MB * 1024 * 1024) {
      setFileError(t("File is too large (max 50 MB)"));
      setCv(null);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setCv(f);
  };

  const clearFile = () => {
    setCv(null);
    setFileError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!content.trim() || submitting) return;
    setSubmitting(true);
    const payload = {
      tender_id: tender.id,
      content: content.trim(),
    };
    if (cv) payload.document = cv;
    const res = await tenderService.createStaffPitch(payload);
    setSubmitting(false);
    if (res.success) {
      setResult({ done: true });
      if (onApplied) onApplied(tender); // flip the card to "Applied"
    } else {
      setResult({ error: res.error || t("Failed to send application") });
    }
  };

  const Body = (
    <div>
      {result && !result.error ? (
        <div className="text-center py-4">
          <div className="mx-auto w-14 h-14 rounded-full bg-[#E7F8EF] flex items-center justify-center mb-4">
            <Check size={26} className="text-[#12B76A]" />
          </div>
          <h3 className="text-[17px] font-semibold text-[#101828]">
            {t("Application sent")}
          </h3>
          <p className="text-[14px] text-[#667085] mt-1">
            {t("The club will review your application")}
          </p>
          <div className="flex mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-[10px] text-white text-[14px] font-medium"
              style={{ backgroundColor: "#2283FF" }}
            >
              {t("Done")}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <h3 className="text-[16px] font-semibold text-[#101828]">
              {t("Apply to this request")}
            </h3>
            <p className="text-[13px] text-[#667085] mt-0.5">
              {positionLabel}
              {clubName ? ` · ${clubName}` : ""}
            </p>
          </div>

          <label className="block text-[13px] font-medium text-[#344054] mb-1.5">
            {t("Cover letter")}
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={t("Introduce yourself and why you're a fit...")}
            className="w-full min-h-[120px] resize-y px-3 py-2.5 border border-[#DDE3EC] rounded-[10px] text-[14px] text-[#101828] placeholder:text-[#98A2B3] focus:outline-none focus:border-[#2283FF]"
          />

          <label className="block text-[13px] font-medium text-[#344054] mt-4 mb-1.5">
            {t("CV")}{" "}
            <span className="text-[#98A2B3] font-normal">({t("optional")})</span>
          </label>
          {cv ? (
            <div className="flex items-center justify-between gap-3 px-3 py-2.5 border border-[#DDE3EC] rounded-[10px]">
              <div className="flex items-center gap-2 min-w-0">
                <Paperclip size={16} className="text-[#667085] shrink-0" />
                <span className="text-[14px] text-[#101828] truncate">
                  {cv.name}
                </span>
              </div>
              <button
                type="button"
                onClick={clearFile}
                className="p-1 text-[#667085] hover:text-[#B42318] shrink-0"
                aria-label={t("Remove CV")}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 border border-dashed border-[#DDE3EC] rounded-[10px] text-[14px] text-[#667085] hover:border-[#2283FF] hover:text-[#2283FF]"
            >
              <Paperclip size={16} />
              {t("Attach CV (PDF)")}
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept=".pdf"
            onChange={handleFile}
            className="hidden"
          />
          {fileError && (
            <p className="text-[13px] text-[#B42318] mt-1.5">{fileError}</p>
          )}

          {result?.error && (
            <p className="text-[13px] text-[#B42318] mt-2">{result.error}</p>
          )}

          <div className="flex gap-3 mt-5">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-[10px] border border-[#DDE3EC] text-[#344054] text-[14px] font-medium"
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              disabled={!content.trim() || submitting}
              className="flex-1 px-4 py-2.5 rounded-[10px] text-white text-[14px] font-medium disabled:opacity-60"
              style={{ backgroundColor: "#2283FF" }}
            >
              {submitting ? t("Sending...") : t("Send application")}
            </button>
          </div>
        </form>
      )}
    </div>
  );

  if (isMobile) {
    // Use the role/club as the sheet title so it isn't a duplicate of the
    // in-body "Apply to this request" heading.
    const sheetTitle =
      result && !result.error
        ? t("Application sent")
        : positionLabel || t("Apply to this request");
    return (
      <BottomSheet open onClose={onClose} title={sheetTitle}>
        <div className="pb-4">{Body}</div>
      </BottomSheet>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-[16px] shadow-[0_20px_60px_rgba(16,24,40,0.24)] w-full max-w-[520px] p-6">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-1 text-[#667085] hover:text-[#101828]"
          aria-label={t("Cancel")}
        >
          <X size={20} />
        </button>
        {Body}
      </div>
    </div>
  );
};

export default LawyerApplyModal;
